/**
 * Data transformation utilities for Platforms Dashboard
 * Transforms raw API data into the format expected by the DataTable component
 */

// Vision UI Dashboard PRO React components
import VuiBox from 'components/VuiBox';
import VuiTypography from 'components/VuiTypography';
import VuiBadge from 'components/VuiBadge';

// React Router
import { Link } from 'react-router-dom';

/**
 * Extract unique root domains from all content and aggregate platform data
 * @param {Array} allContent - All content from the API
 * @returns {Object} - Aggregated platform data
 */
export const aggregatePlatformData = (allContent) => {
  const platformMap = new Map();
  
  allContent.forEach(item => {
    if (!item.post_url) return;
    
    try {
      const urlObj = new URL(item.post_url);
      const rootDomain = urlObj.hostname.replace('www.', '');
      
      if (!platformMap.has(rootDomain)) {
        platformMap.set(rootDomain, {
          domain: rootDomain,
          totalTakedowns: 0,
          complied: 0,
          uncomplied: 0,
          violationTypes: new Set(),
          complianceRate: 0
        });
      }
      
      const platform = platformMap.get(rootDomain);
      platform.totalTakedowns++;
      
      if (item.takedown_status === 'Removed') {
        platform.complied++;
      } else {
        platform.uncomplied++;
      }
      
      if (item.violation_type) {
        platform.violationTypes.add(item.violation_type);
      }
    } catch (e) {
      // Skip invalid URLs
    }
  });
  
  // Calculate compliance rates and convert to array
  const platforms = Array.from(platformMap.values()).map(platform => {
    platform.complianceRate = platform.totalTakedowns > 0 
      ? Math.round((platform.complied / platform.totalTakedowns) * 100) 
      : 0;
    platform.violationTypes = Array.from(platform.violationTypes);
    return platform;
  });
  
  // Sort by total takedowns descending
  return platforms.sort((a, b) => b.totalTakedowns - a.totalTakedowns);
};

/**
 * Transform aggregated platform data into DataTable format
 * @param {Array} platforms - Aggregated platform data
 * @param {Object} pagination - Pagination information
 * @returns {Object} - DataTable compatible format
 */
export const transformPlatformsData = (platforms, pagination = null) => {
  const transformedData = platforms.map((platform) => ({
    platform: (
      <Link to={`/dashboards/platforms/${encodeURIComponent(platform.domain)}/details`} style={{ textDecoration: 'none', color: "white" }}>
        <VuiTypography
          variant="button"
          color="white"
          fontWeight="medium"
          sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
        >
          {platform.domain}
        </VuiTypography>
      </Link>
    ),

    infringementType: (
      <VuiTypography 
        variant="button" 
        color="white" 
        fontWeight="regular"
        sx={{
          wordWrap: 'break-word',
          whiteSpace: 'normal',
          lineHeight: 1.5
        }}
      >
        {platform.violationTypes.length > 0 
          ? platform.violationTypes.join(', ') 
          : 'N/A'}
      </VuiTypography>
    ),

    complianceStatus: (
      <VuiBox display="flex" alignItems="center" gap={1}>
        <VuiBox 
          width="8px" 
          height="8px" 
          borderRadius="50%" 
          bgColor={
            platform.complianceRate >= 50 ? 'success' :
            platform.complianceRate >= 25 ? 'warning' : 'error'
          }
        />
        <VuiTypography variant="button" color="white" fontWeight="regular">
          {platform.complianceRate >= 50 ? 'Good' : 
           platform.complianceRate >= 25 ? 'Fair' : 'Poor'}
        </VuiTypography>
      </VuiBox>
    ),

    totalTakedowns: (
      <VuiTypography variant="button" color="white" fontWeight="medium">
        {platform.totalTakedowns}
      </VuiTypography>
    ),

    complianceRate: (
      <VuiTypography variant="button" color="white" fontWeight="regular">
        {platform.complianceRate}%
      </VuiTypography>
    ),

    priority: (
      <VuiBox display="flex" alignItems="center" gap={1}>
        <VuiBox 
          width="8px" 
          height="8px" 
          borderRadius="50%" 
          bgColor={
            platform.complianceRate < 25 ? 'error' :
            platform.complianceRate < 50 ? 'warning' : 'success'
          }
        />
        <VuiTypography variant="button" color="white" fontWeight="regular">
          {platform.complianceRate < 25 ? 'High' : 
           platform.complianceRate < 50 ? 'Medium' : 'Low'}
        </VuiTypography>
      </VuiBox>
    ),

    // Store raw data for sorting and filtering
    _rawData: platform,
  }));

  return {
    columns: [
      { Header: 'PLATFORM', accessor: 'platform', width: '15%' },
      { Header: 'INFRINGEMENT TYPE', accessor: 'infringementType', width: '20%' },
      { Header: 'COMPLIANCE STATUS', accessor: 'complianceStatus', width: '12%' },
      { Header: '# TAKEDOWNS SUBMITTED', accessor: 'totalTakedowns', width: '12%' },
      { Header: 'COMPLIANCE RATE', accessor: 'complianceRate', width: '10%' },
      { Header: 'PRIORITY', accessor: 'priority', width: '10%' },
    ],
    rows: transformedData,
    pagination: pagination,
  };
};

/**
 * Get filter buttons with counts for the platforms dashboard
 * @param {Object} counts - Count data from the API
 * @param {Function} onFilterChange - Callback for filter changes
 * @param {string} activeFilter - Currently active filter
 * @returns {Array} - Array of filter button objects
 */
export const getPlatformsFilterButtons = (counts, onFilterChange, activeFilter) => {
  if (!counts?.breakdown) {
    return [
      { key: 'poorCompliance', label: 'Poor Compliance', filter: { compliance_rate: 'poor' } },
      { key: 'Explicit', label: 'Explicit', filter: { violation_type: 'Explicit' } },
      { key: 'Copyright', label: 'Copyright', filter: { violation_type: 'Copyright' } }
    ];
  }

  const filterButtons = [];

  // Add compliance-based filters
  if (counts.breakdown.by_compliance) {
    const poorCompliance = counts.breakdown.by_compliance.find(item => item.compliance === 'poor');
    if (poorCompliance && poorCompliance.count > 0) {
      filterButtons.push({
        key: 'poorCompliance',
        label: `Poor Compliance (${poorCompliance.count})`,
        filter: { compliance_rate: 'poor' },
        count: poorCompliance.count,
      });
    }
  }

  // Add violation type filters
  if (counts.breakdown.by_violation_type) {
    counts.breakdown.by_violation_type.forEach((item) => {
      if (item.count > 0) {
        filterButtons.push({
          key: item.violation_type,
          label: `${item.violation_type} (${item.count})`,
          filter: { violation_type: item.violation_type },
          count: item.count,
        });
      }
    });
  }

  return filterButtons;
};

/**
 * Create empty state for platforms dashboard
 * @returns {Object} - Empty DataTable format
 */
export const createEmptyPlatformsData = () => ({
  columns: [
    { Header: 'PLATFORM', accessor: 'platform', width: '15%' },
    { Header: 'INFRINGEMENT TYPE', accessor: 'infringementType', width: '20%' },
    { Header: 'COMPLIANCE STATUS', accessor: 'complianceStatus', width: '12%' },
    { Header: '# TAKEDOWNS SUBMITTED', accessor: 'totalTakedowns', width: '12%' },
    { Header: 'COMPLIANCE RATE', accessor: 'complianceRate', width: '10%' },
    { Header: 'PRIORITY', accessor: 'priority', width: '10%' },
  ],
  rows: [],
  pagination: {
    current_page: 1,
    total_pages: 1,
    total_items: 0,
    items_per_page: 20,
    has_next_page: false,
    has_prev_page: false,
  },
});
